/**
 * Generic Pagination Service
 *
 * Utility service for handling pagination transformations and query building.
 * Provides reusable functions for converting between API and frontend formats.
 *
 * @module services/shared/PaginationService
 */

import type {
  PaginatedApiResponse,
  PaginatedResponse,
  PaginationMeta,
  PaginationParams,
} from '@/types/pagination';
import { DEFAULT_PAGINATION } from '@/types/pagination';

/**
 * Transform snake_case pagination metadata to camelCase
 */
export function transformPaginationMeta(
  page: number,
  per_page: number,
  total: number,
  total_pages: number
): PaginationMeta {
  return {
    page,
    perPage: per_page,
    total,
    totalPages: total_pages,
  };
}

/**
 * Transform paginated API response to frontend format
 */
export function transformPaginatedResponse<TApi, TFrontend>(
  apiResponse: PaginatedApiResponse<TApi>,
  transformItem: (item: TApi) => TFrontend
): PaginatedResponse<TFrontend> {
  return {
    data: apiResponse.data.map(transformItem),
    pagination: transformPaginationMeta(
      apiResponse.page,
      apiResponse.per_page,
      apiResponse.total,
      apiResponse.total_pages
    ),
  };
}

/**
 * Build URL query parameters for pagination
 */
export function buildPaginationQuery(params: PaginationParams): URLSearchParams {
  const queryParams = new URLSearchParams();

  // Add pagination parameters
  if (params.page && params.page > 1) {
    queryParams.append('page', params.page.toString());
  }

  if (params.perPage && params.perPage !== DEFAULT_PAGINATION.PAGE_SIZE) {
    queryParams.append('per_page', params.perPage.toString());
  }

  // Add search parameter
  if (params.search && params.search.trim()) {
    queryParams.append('search', params.search.trim());
  }

  // Add sorting parameters
  if (params.sortBy) {
    queryParams.append('sort_by', params.sortBy);
  }

  if (params.sortOrder && params.sortOrder !== 'asc') {
    queryParams.append('sort_order', params.sortOrder);
  }

  return queryParams;
}

/**
 * Build query string from pagination parameters
 */
export function buildQueryString(params: PaginationParams): string {
  const queryParams = buildPaginationQuery(params);
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Validate pagination parameters
 */
export function validatePaginationParams(params: PaginationParams): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Validate page number
  if (params.page !== undefined) {
    if (!Number.isInteger(params.page) || params.page < 1) {
      errors.push('Page number must be a positive integer');
    }
  }

  // Validate page size
  if (params.perPage !== undefined) {
    if (!Number.isInteger(params.perPage) || params.perPage < 1) {
      errors.push('Page size must be a positive integer');
    } else if (params.perPage > DEFAULT_PAGINATION.MAX_PAGE_SIZE) {
      errors.push(`Page size cannot exceed ${DEFAULT_PAGINATION.MAX_PAGE_SIZE}`);
    } else if (params.perPage < DEFAULT_PAGINATION.MIN_PAGE_SIZE) {
      errors.push(`Page size cannot be less than ${DEFAULT_PAGINATION.MIN_PAGE_SIZE}`);
    }
  }

  // Validate sort order
  if (params.sortOrder && !['asc', 'desc'].includes(params.sortOrder)) {
    errors.push('Sort order must be either "asc" or "desc"');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Normalize pagination parameters with defaults
 */
export function normalizePaginationParams(params: PaginationParams): Required<PaginationParams> {
  return {
    page: params.page || DEFAULT_PAGINATION.PAGE,
    perPage: params.perPage || DEFAULT_PAGINATION.PAGE_SIZE,
    search: params.search || '',
    sortBy: params.sortBy || '',
    sortOrder: params.sortOrder || 'asc',
  };
}

/**
 * Calculate offset for database queries
 */
export function calculateOffset(page: number, perPage: number): number {
  return (page - 1) * perPage;
}

/**
 * Calculate page number from offset
 */
export function calculatePageFromOffset(offset: number, perPage: number): number {
  return Math.floor(offset / perPage) + 1;
}

/**
 * Get pagination info for display
 */
export function getPaginationInfo(
  page: number,
  perPage: number,
  total: number
): {
  start: number;
  end: number;
  total: number;
  showing: string;
} {
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);

  return {
    start,
    end,
    total,
    showing: total === 0
      ? 'No items found'
      : `Showing ${start}-${end} of ${total} items`,
  };
}

/**
 * Check if pagination response is empty
 */
export function isPaginationEmpty<T>(response: PaginatedResponse<T>): boolean {
  return response.data.length === 0 || response.pagination.total === 0;
}

/**
 * Check if pagination has more pages
 */
export function hasMorePages(pagination: PaginationMeta): boolean {
  return pagination.page < pagination.totalPages;
}

/**
 * Check if pagination has previous pages
 */
export function hasPreviousPages(pagination: PaginationMeta): boolean {
  return pagination.page > 1;
}

/**
 * Pagination Service class with utility methods
 */
export class PaginationService {
  /**
   * Transform API response to frontend format
   */
  static transform<TApi, TFrontend>(
    apiResponse: PaginatedApiResponse<TApi>,
    transformItem: (item: TApi) => TFrontend
  ): PaginatedResponse<TFrontend> {
    return transformPaginatedResponse(apiResponse, transformItem);
  }

  /**
   * Build query string for API requests
   */
  static buildQuery(params: PaginationParams): string {
    return buildQueryString(params);
  }

  /**
   * Validate pagination parameters
   */
  static validate(params: PaginationParams): { isValid: boolean; errors: string[] } {
    return validatePaginationParams(params);
  }

  /**
   * Normalize parameters with defaults
   */
  static normalize(params: PaginationParams): Required<PaginationParams> {
    return normalizePaginationParams(params);
  }

  /**
   * Get display information
   */
  static getInfo(page: number, perPage: number, total: number) {
    return getPaginationInfo(page, perPage, total);
  }
}

export default PaginationService;